/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  ChatAgentList,
  ChatBan,
  ChatDepartment,
  ChatSettings,
  ChatShortcut,
  ChatTrigger,
  CreateChatBanPathParameters,
  CreateChatBanRequest,
  CreateChatDepartmentPathParameters,
  CreateChatDepartmentRequest,
  CreateChatShortcutPathParameters,
  CreateChatShortcutRequest,
  CreateChatTriggerPathParameters,
  CreateChatTriggerRequest,
  DeleteChatBanPathParameters,
  DeleteChatDepartmentPathParameters,
  DeleteChatShortcutPathParameters,
  DeleteChatTriggerPathParameters,
  GetChatSettingsPathParameters,
  GetMyChatProfilePathParameters,
  ListChatAgentsPathParameters,
  ListChatBans200,
  ListChatBansParams,
  ListChatBansPathParameters,
  ListChatDepartments200,
  ListChatDepartmentsPathParameters,
  ListChatShortcuts200,
  ListChatShortcutsPathParameters,
  ListChatTriggers200,
  ListChatTriggersPathParameters,
  MemberChatProfile,
  RenderChatShortcutPathParameters,
  RenderedChatShortcut,
  UpdateChatDepartmentPathParameters,
  UpdateChatDepartmentRequest,
  UpdateChatSettingsPathParameters,
  UpdateChatSettingsRequest,
  UpdateChatShortcutPathParameters,
  UpdateChatShortcutRequest,
  UpdateChatTriggerPathParameters,
  UpdateChatTriggerRequest,
  UpdateMemberChatProfileRequest,
  UpdateMyChatProfilePathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary How you appear to this store's shoppers
 */
export const getMyChatProfile = (
    { storeId }: GetMyChatProfilePathParameters,
 options?: SecondParameter<typeof customInstance<MemberChatProfile>>,) => {
      return customInstance<MemberChatProfile>(
      {url: `/admin/v1/stores/${storeId}/chat/profile`, method: 'GET'
    },
      options);
    }
  /**
 * null displayName shows your first name; the avatar is a media library id
 * @summary Set your chat name and avatar for this store
 */
export const updateMyChatProfile = (
    { storeId }: UpdateMyChatProfilePathParameters,
    updateMemberChatProfileRequest: BodyType<UpdateMemberChatProfileRequest>,
 options?: SecondParameter<typeof customInstance<MemberChatProfile>>,) => {
      return customInstance<MemberChatProfile>(
      {url: `/admin/v1/stores/${storeId}/chat/profile`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: updateMemberChatProfileRequest
    },
      options);
    }
  /**
 * settings.chat of the store with every default filled in, the same object GET /org/stores/{id} carries, readable with conversations.read
 * @summary The store's live chat settings
 */
export const getChatSettings = (
    { storeId }: GetChatSettingsPathParameters,
 options?: SecondParameter<typeof customInstance<ChatSettings>>,) => {
      return customInstance<ChatSettings>(
      {url: `/admin/v1/stores/${storeId}/chat/settings`, method: 'GET'
    },
      options);
    }
  /**
 * Needs conversations.manage instead of the store PATCH's settings.write. Merged one group deep exactly like settings.chat in PATCH /org/stores/{id}: an omitted group or key keeps its value, form field lists and the business-hours week are replaced whole. Saves are serialised on the store row, so concurrent saves of different groups all land. Answers the whole resolved settings; 409 for a closed store
 * @summary Change the store's live chat settings
 */
export const updateChatSettings = (
    { storeId }: UpdateChatSettingsPathParameters,
    updateChatSettingsRequest: BodyType<UpdateChatSettingsRequest>,
 options?: SecondParameter<typeof customInstance<ChatSettings>>,) => {
      return customInstance<ChatSettings>(
      {url: `/admin/v1/stores/${storeId}/chat/settings`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateChatSettingsRequest
    },
      options);
    }
  /**
 * Active members whose store access covers this store and whose role holds conversations.read or conversations.write, with their chat name and avatar here; canReply marks conversations.write (assignable, counted as agents). For the department member and assignment pickers, without staff.read. No emails
 * @summary Staff who can work this store's chat
 */
export const listChatAgents = (
    { storeId }: ListChatAgentsPathParameters,
 options?: SecondParameter<typeof customInstance<ChatAgentList>>,) => {
      return customInstance<ChatAgentList>(
      {url: `/admin/v1/stores/${storeId}/chat/agents`, method: 'GET'
    },
      options);
    }
  /**
 * Organisation-wide and store shortcuts plus the caller's own, by key. Bounded: at most 500 per organization
 * @summary Shortcuts usable on this store
 */
export const listChatShortcuts = (
    { storeId }: ListChatShortcutsPathParameters,
 options?: SecondParameter<typeof customInstance<ListChatShortcuts200>>,) => {
      return customInstance<ListChatShortcuts200>(
      {url: `/admin/v1/stores/${storeId}/chat/shortcuts`, method: 'GET'
    },
      options);
    }
  /**
 * personal needs conversations.write; store and organization need conversations.manage, and organization also access to every store. 409 ALREADY_EXISTS when the key is taken at that scope
 * @summary Add a shortcut
 */
export const createChatShortcut = (
    { storeId }: CreateChatShortcutPathParameters,
    createChatShortcutRequest: BodyType<CreateChatShortcutRequest>,
 options?: SecondParameter<typeof customInstance<ChatShortcut>>,) => {
      return customInstance<ChatShortcut>(
      {url: `/admin/v1/stores/${storeId}/chat/shortcuts`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createChatShortcutRequest
    },
      options);
    }
  /**
 * Someone else's personal shortcut answers 404
 * @summary Change a shortcut
 */
export const updateChatShortcut = (
    { storeId, id }: UpdateChatShortcutPathParameters,
    updateChatShortcutRequest: BodyType<UpdateChatShortcutRequest>,
 options?: SecondParameter<typeof customInstance<ChatShortcut>>,) => {
      return customInstance<ChatShortcut>(
      {url: `/admin/v1/stores/${storeId}/chat/shortcuts/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateChatShortcutRequest
    },
      options);
    }
  /**
 * @summary Remove a shortcut
 */
export const deleteChatShortcut = (
    { storeId, id }: DeleteChatShortcutPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/chat/shortcuts/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Unknown placeholders and values the conversation does not have render empty
 * @summary A shortcut with its placeholders filled for one conversation
 */
export const renderChatShortcut = (
    { storeId, conversationId, id }: RenderChatShortcutPathParameters,
 options?: SecondParameter<typeof customInstance<RenderedChatShortcut>>,) => {
      return customInstance<RenderedChatShortcut>(
      {url: `/admin/v1/stores/${storeId}/conversations/${conversationId}/shortcuts/${id}/render`, method: 'GET'
    },
      options);
    }
  /**
 * Bounded list, at most 50, by position
 * @summary Chat departments of the store
 */
export const listChatDepartments = (
    { storeId }: ListChatDepartmentsPathParameters,
 options?: SecondParameter<typeof customInstance<ListChatDepartments200>>,) => {
      return customInstance<ListChatDepartments200>(
      {url: `/admin/v1/stores/${storeId}/chat/departments`, method: 'GET'
    },
      options);
    }
  /**
 * Members must be active staff with access to the store. Shoppers pick a department in a pre-chat form field of kind department
 * @summary Add a department
 */
export const createChatDepartment = (
    { storeId }: CreateChatDepartmentPathParameters,
    createChatDepartmentRequest: BodyType<CreateChatDepartmentRequest>,
 options?: SecondParameter<typeof customInstance<ChatDepartment>>,) => {
      return customInstance<ChatDepartment>(
      {url: `/admin/v1/stores/${storeId}/chat/departments`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createChatDepartmentRequest
    },
      options);
    }
  /**
 * @summary Rename, reorder, make default or replace members
 */
export const updateChatDepartment = (
    { storeId, id }: UpdateChatDepartmentPathParameters,
    updateChatDepartmentRequest: BodyType<UpdateChatDepartmentRequest>,
 options?: SecondParameter<typeof customInstance<ChatDepartment>>,) => {
      return customInstance<ChatDepartment>(
      {url: `/admin/v1/stores/${storeId}/chat/departments/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateChatDepartmentRequest
    },
      options);
    }
  /**
 * Its conversations stay, without a department
 * @summary Remove a department
 */
export const deleteChatDepartment = (
    { storeId, id }: DeleteChatDepartmentPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/chat/departments/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Expired bans stay listed until removed
 * @summary Banned visitors and addresses, newest first
 */
export const listChatBans = (
    { storeId }: ListChatBansPathParameters,
    params?: ListChatBansParams,
 options?: SecondParameter<typeof customInstance<ListChatBans200>>,) => {
      return customInstance<ListChatBans200>(
      {url: `/admin/v1/stores/${storeId}/chat/bans`, method: 'GET',
        params
    },
      options);
    }
  /**
 * A banned visitor still sees a working widget; what they send is kept out of the inbox and fires no events. The reason is never shown to the shopper
 * @summary Ban a visitor or an IP address
 */
export const createChatBan = (
    { storeId }: CreateChatBanPathParameters,
    createChatBanRequest: BodyType<CreateChatBanRequest>,
 options?: SecondParameter<typeof customInstance<ChatBan>>,) => {
      return customInstance<ChatBan>(
      {url: `/admin/v1/stores/${storeId}/chat/bans`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createChatBanRequest
    },
      options);
    }
  /**
 * @summary Lift a ban
 */
export const deleteChatBan = (
    { storeId, id }: DeleteChatBanPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/chat/bans/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Bounded: at most 30 per store
 * @summary The store's triggers by position
 */
export const listChatTriggers = (
    { storeId }: ListChatTriggersPathParameters,
 options?: SecondParameter<typeof customInstance<ListChatTriggers200>>,) => {
      return customInstance<ListChatTriggers200>(
      {url: `/admin/v1/stores/${storeId}/chat/triggers`, method: 'GET'
    },
      options);
    }
  /**
 * Members sent as must be able to reply in this store's chat, products must be published here and discounts active code discounts (422 INVALID_REFERENCE with the action's field). A trigger with a discount is stored as once per visitor. 422 LIMIT_EXCEEDED past 30
 * @summary Add a trigger
 */
export const createChatTrigger = (
    { storeId }: CreateChatTriggerPathParameters,
    createChatTriggerRequest: BodyType<CreateChatTriggerRequest>,
 options?: SecondParameter<typeof customInstance<ChatTrigger>>,) => {
      return customInstance<ChatTrigger>(
      {url: `/admin/v1/stores/${storeId}/chat/triggers`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createChatTriggerRequest
    },
      options);
    }
  /**
 * The fields sent replace the stored ones; the result is checked like a new trigger
 * @summary Change a trigger
 */
export const updateChatTrigger = (
    { storeId, id }: UpdateChatTriggerPathParameters,
    updateChatTriggerRequest: BodyType<UpdateChatTriggerRequest>,
 options?: SecondParameter<typeof customInstance<ChatTrigger>>,) => {
      return customInstance<ChatTrigger>(
      {url: `/admin/v1/stores/${storeId}/chat/triggers/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateChatTriggerRequest
    },
      options);
    }
  /**
 * Messages it already posted stay in their conversations
 * @summary Remove a trigger
 */
export const deleteChatTrigger = (
    { storeId, id }: DeleteChatTriggerPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/chat/triggers/${id}`, method: 'DELETE'
    },
      options);
    }
  export type GetMyChatProfileResult = NonNullable<Awaited<ReturnType<typeof getMyChatProfile>>>
export type UpdateMyChatProfileResult = NonNullable<Awaited<ReturnType<typeof updateMyChatProfile>>>
export type GetChatSettingsResult = NonNullable<Awaited<ReturnType<typeof getChatSettings>>>
export type UpdateChatSettingsResult = NonNullable<Awaited<ReturnType<typeof updateChatSettings>>>
export type ListChatAgentsResult = NonNullable<Awaited<ReturnType<typeof listChatAgents>>>
export type ListChatShortcutsResult = NonNullable<Awaited<ReturnType<typeof listChatShortcuts>>>
export type CreateChatShortcutResult = NonNullable<Awaited<ReturnType<typeof createChatShortcut>>>
export type UpdateChatShortcutResult = NonNullable<Awaited<ReturnType<typeof updateChatShortcut>>>
export type DeleteChatShortcutResult = NonNullable<Awaited<ReturnType<typeof deleteChatShortcut>>>
export type RenderChatShortcutResult = NonNullable<Awaited<ReturnType<typeof renderChatShortcut>>>
export type ListChatDepartmentsResult = NonNullable<Awaited<ReturnType<typeof listChatDepartments>>>
export type CreateChatDepartmentResult = NonNullable<Awaited<ReturnType<typeof createChatDepartment>>>
export type UpdateChatDepartmentResult = NonNullable<Awaited<ReturnType<typeof updateChatDepartment>>>
export type DeleteChatDepartmentResult = NonNullable<Awaited<ReturnType<typeof deleteChatDepartment>>>
export type ListChatBansResult = NonNullable<Awaited<ReturnType<typeof listChatBans>>>
export type CreateChatBanResult = NonNullable<Awaited<ReturnType<typeof createChatBan>>>
export type DeleteChatBanResult = NonNullable<Awaited<ReturnType<typeof deleteChatBan>>>
export type ListChatTriggersResult = NonNullable<Awaited<ReturnType<typeof listChatTriggers>>>
export type CreateChatTriggerResult = NonNullable<Awaited<ReturnType<typeof createChatTrigger>>>
export type UpdateChatTriggerResult = NonNullable<Awaited<ReturnType<typeof updateChatTrigger>>>
export type DeleteChatTriggerResult = NonNullable<Awaited<ReturnType<typeof deleteChatTrigger>>>
